import { useCallback, useEffect, useMemo, useState } from 'react'
import { TextField } from '@mui/material'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@vapor/material'
import { FormItem } from 'hic-components'
import { useTranslation } from 'react-i18next'
import { CustomerInput, CustomerQuery, CustomerQueryVariables, CustomerType, ProductionAssignedInput, useCustomerNewMutation, useCustomerQuery } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useCurrentLocale from 'src/app/hooks/current-locale/use-current-locale'
import CustomerTypeLookup from './bill-creation-lookup'
import { ReservationF } from '../../reservation-detail/types/reservation-detail.types'
import { ReservedResourceWithCustomersSelect } from 'src/app/hooks/reserved-resources/rr-with-customers-select'
import { NOT_SELECTED } from '../../reservation-detail/reserved-resource-filter/reserved-resource-filter'
import { BaseCustomerLookup } from 'src/app/components/customer-lookup/base/base-customer-lookup'
import { QueryHookOptions } from '@apollo/client'

type BillCreationModalProps = {
  open: boolean
  setOpen: (value: boolean) => void
  title: string
  toEditBill?: BillData
  cancelAction: () => void
  deleteAction?: (passerbyBillId: string) => void
  confirmAction: (passerbyBill: BillData) => void
  reservation?: ReservationF
}

export type BillData = {
  billId?: string
  ownerId: string
  description: string
  productionLength?: number | null
  reservedResourceId?: string | null //TODO: Verificare se serve portarsi questo campo nell'edit (si utilizza anche per i conti NON passanti????)
}

export default function BillCreationModal(props: BillCreationModalProps) {
  const { open, setOpen, title, toEditBill, cancelAction, deleteAction, confirmAction } = props
  const { t } = useTranslation()
  const { hicLanguage } = useCurrentLocale()
  const { id: tenantId } = useTenant()

  const [description, setDescription] = useState<string | undefined>(toEditBill?.description)
  const [owner, setOwner] = useState<BaseCustomerLookup | undefined>()

  const [reservedResourceReference, setReservedResourceReference] = useState<string>()

  const [showOwnerIdError, setShowOwnerIdError] = useState<string | null>(null)
  const [showDescriptionError, setShowDescriptionError] = useState<string | null>(null)

  const [createM] = useCustomerNewMutation()
  const createNewUser = useCallback(
    async (userData: { firstName: string; lastName: string; type: CustomerType }) => {
      const input: CustomerInput = {
        tenantId: tenantId,
        lastName: userData.lastName,
        firstName: userData.firstName,
        language: hicLanguage,
        name: `${userData.lastName} ${userData.firstName}`,
        type: userData.type,
      }
      try {
        const result = await createM({ variables: { input: input } })
        const customer = result.data?.createCustomer
        if (!customer) {
          return { type: 'FAIL', errors: result.errors }
        }
        return { type: 'SUCCESS', customer }
      } catch (e) {
        return { type: 'FAIL' }
      }
    },
    [tenantId, createM],
  )

  useEffect(() => {
    if (showOwnerIdError) {
      setShowOwnerIdError(null)
    }
  }, [owner])

  useEffect(() => {
    if (showDescriptionError) {
      setShowDescriptionError(null)
    }
  }, [description])

  const customerQueryFilter = useMemo((): QueryHookOptions<CustomerQuery, CustomerQueryVariables> => {
    if (toEditBill && toEditBill.ownerId) {
      return {
        variables: {
          tenantId: tenantId,
          customerId: toEditBill.ownerId,
        },
      }
    }
    return {
      skip: true,
    }
  }, [tenantId, toEditBill])

  const { data: customerData, loading: customerLoading } = useCustomerQuery({ ...customerQueryFilter })

  useEffect(() => {
    if (!open) {
      setDescription('')
      setOwner(undefined)
    } else if (toEditBill?.ownerId && customerData?.customers?.[0]) {
      const customer = customerData.customers[0]
      setDescription(toEditBill.description.replace(`${customer.name} - `, ''))
      setOwner({ id: customer.id, name: customer.name, type: customer.type })
    }
  }, [open, toEditBill, customerData])

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth={true} maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12} mt={1}>
            <FormItem
              fieldLabel={t('pms.booking.passerby-bill.creation-dialog.description')}
              fullWidth
              errorMessage={showDescriptionError ? t('pms.booking.passerby-bill.creation-dialog.field-empty-error') : undefined}
            >
              <TextField
                sx={{ flexGrow: 1 }}
                value={description ?? ''}
                onChange={(event) => setDescription(event.target.value)}
                // InputProps={{
                //   startAdornment: <InputAdornment position="start">{owner?.name ? `${owner.name} -` : <></>}</InputAdornment>,
                // }}
              />
            </FormItem>
          </Grid>
          <Grid item xs={12}>
            <FormItem fieldLabel={t('pms.booking.passerby-bill.creation-dialog.owner')} errorMessage={showOwnerIdError ?? undefined} fullWidth>
              {!props.reservation ? (
                <CustomerTypeLookup
                  disabled={customerLoading}
                  value={owner ?? null}
                  onChange={(value) => setOwner(value ?? undefined)}
                  onCreateClick={async (value) => {
                    const createdUser = await createNewUser(value)
                    if (createdUser.type === 'ERROR') {
                      setShowOwnerIdError(t('pms.booking.passerby-bill.creation-dialog.field-customer-creation-error'))
                    } else if (createdUser.customer) {
                      setOwner({ ...createdUser.customer })
                    }
                  }}
                />
              ) : (
                <ReservedResourceWithCustomersSelect
                  reservation={props.reservation}
                  value={`${reservedResourceReference ?? NOT_SELECTED}#${owner?.id}`}
                  onSelectChange={(guestId, rrId) => {
                    if (rrId) {
                      const rr = props.reservation?.reservedResources?.find((rr) => rr.id === rrId)
                      if (rr) {
                        setReservedResourceReference(rr.id)
                        const guestOccupancy = rr.guestOccupancies?.find((g) => g.guest.id === guestId)
                        if (guestOccupancy) setOwner({ ...guestOccupancy.guest })
                      }
                    } else {
                      const customerReference = props.reservation?.customerReferenceList.find((cr) => cr.id === guestId)
                      if (customerReference) setOwner({ ...customerReference })
                      setReservedResourceReference(undefined)
                    }
                  }}
                />
              )}
            </FormItem>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            cancelAction?.()
          }}
          variant="outlined"
        >
          {t('commons.cancel')}
        </Button>
        {deleteAction !== undefined && toEditBill?.billId !== undefined && (
          <Button
            onClick={() => {
              if (toEditBill.billId) deleteAction(toEditBill.billId)
            }}
            variant="contained"
            color="error"
          >
            {t('commons.delete')}
          </Button>
        )}
        <Button
          onClick={() => {
            if (owner?.id && owner.name && description) {
              confirmAction({
                billId: props.toEditBill?.billId,
                ownerId: owner.id,
                description: description,
                reservedResourceId: reservedResourceReference,
              })
            } else {
              setShowOwnerIdError(owner?.id === undefined || owner?.id.replace(' ', '').length === 0 ? t('pms.booking.passerby-bill.creation-dialog.field-empty-error') : null)
              setShowDescriptionError(description === undefined || description.replace(' ', '').length === 0 ? t('pms.booking.passerby-bill.creation-dialog.field-empty-error') : null)
            }
          }}
          variant="contained"
        >
          {t('commons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
